import { useShow } from '@refinedev/core';
import { Card, Col, Descriptions, Row, Space, Tag, Typography, Button, Popconfirm, message, Modal, Input } from 'antd';
import { ArrowLeftOutlined, UserOutlined, CarOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { apiClient } from '../../lib/api-client';
import { t } from '../../i18n';

const { Title, Text } = Typography;

const statusLabels: Record<string, string> = {
  PENDING: t('status.pending'),
  CONFIRMED: t('status.approved'),
  CANCELLED: t('status.cancelled'),
  COMPLETED: t('status.completed'),
  ON_THE_WAY: t('status.onTheWay'),
  ARRIVED: t('status.driverArrived'),
  IN_PROGRESS: t('status.inProgress'),
};

const statusColors: Record<string, string> = {
  PENDING: 'gold',
  CONFIRMED: 'blue',
  CANCELLED: 'red',
  COMPLETED: 'green',
  ON_THE_WAY: 'orange',
  ARRIVED: 'cyan',
  IN_PROGRESS: 'purple',
};

const tripModeLabels: Record<string, string> = {
  SHAHARLAR_ARO: 'Viloyat ichida',
  VILOYATLAR_ARO: 'Viloyatlar aro',
};

const formatPrice = (price: number) => {
  return new Intl.NumberFormat('uz-UZ').format(price) + " so'm";
};

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('uz-UZ', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const DriverTripBookingShow = () => {
  const navigate = useNavigate();
  const { queryResult } = useShow({ resource: 'admin/driver-trip-bookings' });
  const { data, isLoading } = queryResult;
  const booking = data?.data as any;
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('Admin tomonidan bekor qilindi');
  const [cancelling, setCancelling] = useState(false);

  const handleConfirm = async () => {
    try {
      await apiClient.put(`/admin/driver-trip-bookings/${booking.id}`, { status: 'CONFIRMED' });
      message.success(t('common.success'));
      queryResult.refetch();
    } catch {
      message.error(t('common.error'));
    }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      message.error('Sabab kiritilishi shart');
      return;
    }
    setCancelling(true);
    try {
      await apiClient.put(`/admin/driver-trip-bookings/${booking.id}`, {
        status: 'CANCELLED',
        cancelReason: cancelReason,
      });
      message.success(t('common.success'));
      setCancelModalOpen(false);
      setCancelReason('Admin tomonidan bekor qilindi');
      queryResult.refetch();
    } catch {
      message.error(t('common.error'));
    } finally {
      setCancelling(false);
    }
  };

  if (isLoading || !booking) {
    return <Card loading />;
  }

  const trip = booking.driverPostedTrip;

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/driver-trip-bookings')}>
          Orqaga
        </Button>
        <Title level={4} style={{ margin: 0 }}>
          Buyurtma #{booking.id}
        </Title>
        <Tag color={statusColors[booking.status] || 'default'} style={{ fontSize: 14, padding: '2px 12px' }}>
          {statusLabels[booking.status] || booking.status}
        </Tag>
      </Space>

      <Row gutter={[16, 16]}>
        {/* Left column */}
        <Col lg={10} xs={24}>
          {/* Booking Info */}
          <Card title={t('nav.bookings')} size="small" style={{ marginBottom: 16 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Holat">
                <Tag color={statusColors[booking.status] || 'default'}>
                  {statusLabels[booking.status] || booking.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Joylar soni">{booking.seatsBooked}</Descriptions.Item>
              <Descriptions.Item label="Jami summa">{formatPrice(booking.totalPrice)}</Descriptions.Item>
              <Descriptions.Item label="To'lov usuli">{booking.paymentMethod || '-'}</Descriptions.Item>
              <Descriptions.Item label="To'lov holati">
                <Tag color={booking.isPaid ? 'green' : 'orange'}>
                  {booking.isPaid ? "To'langan" : "To'lanmagan"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Yaratilgan">{formatDate(booking.createdAt)}</Descriptions.Item>
              {booking.confirmedAt && (
                <Descriptions.Item label="Tasdiqlangan">{formatDate(booking.confirmedAt)}</Descriptions.Item>
              )}
              {booking.cancelledAt && (
                <Descriptions.Item label="Bekor qilingan">{formatDate(booking.cancelledAt)}</Descriptions.Item>
              )}
              {booking.cancelReason && (
                <Descriptions.Item label="Bekor qilish sababi">
                  <Text type="danger">{booking.cancelReason}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {/* Passenger Info */}
          <Card title={<Space><UserOutlined /> Yo'lovchi</Space>} size="small" style={{ marginBottom: 16 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Ism">{booking.passenger?.fullName || '-'}</Descriptions.Item>
              <Descriptions.Item label="Telefon">{booking.passenger?.phone || '-'}</Descriptions.Item>
              {booking.passenger?.rating && (
                <Descriptions.Item label="Reyting">{Number(booking.passenger.rating).toFixed(1)}</Descriptions.Item>
              )}
              {booking.passengerNote && (
                <Descriptions.Item label="Izoh">{booking.passengerNote}</Descriptions.Item>
              )}
              {booking.pickupAddress && (
                <Descriptions.Item label="Olib ketish joyi">
                  <Space direction="vertical" size={0}>
                    <Text>{booking.pickupAddress}</Text>
                    {booking.pickupLat && booking.pickupLong && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {Number(booking.pickupLat).toFixed(6)}, {Number(booking.pickupLong).toFixed(6)}
                      </Text>
                    )}
                  </Space>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {/* Actions */}
          {booking.status === 'PENDING' && (
            <Card title="Amallar" size="small">
              <Space>
                <Popconfirm
                  title="Buyurtmani tasdiqlaysizmi?"
                  onConfirm={handleConfirm}
                  okText="Ha"
                  cancelText="Yo'q"
                >
                  <Button type="primary">{t('common.approve')}</Button>
                </Popconfirm>
                <Button danger onClick={() => setCancelModalOpen(true)}>{t('common.cancel')}</Button>
              </Space>
            </Card>
          )}
          {booking.status === 'CONFIRMED' && (
            <Card title="Amallar" size="small">
              <Button danger onClick={() => setCancelModalOpen(true)}>Bekor qilish</Button>
            </Card>
          )}
        </Col>

        {/* Right column - Trip Info */}
        <Col lg={14} xs={24}>
          {trip && (
            <>
              {/* Route Info */}
              <Card
                title={<Space><EnvironmentOutlined /> Safar #{trip.id}</Space>}
                size="small"
                style={{ marginBottom: 16 }}
                extra={
                  <Button type="link" onClick={() => navigate(`/driver-posted-trips/${trip.id}`)}>
                    Safarni ko'rish
                  </Button>
                }
              >
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Rejim">
                    <Tag color={trip.tripMode === 'SHAHARLAR_ARO' ? 'blue' : 'purple'}>
                      {tripModeLabels[trip.tripMode] || trip.tripMode}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Qayerdan">
                    <Space direction="vertical" size={0}>
                      <Text strong>{trip.departureRegion?.nameUz || '-'}</Text>
                      <Text type="secondary">{trip.departureCity?.nameUz || trip.departureDistrict?.nameUz || ''}</Text>
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label="Qayerga">
                    <Space direction="vertical" size={0}>
                      <Text strong>{trip.destinationRegion?.nameUz || '-'}</Text>
                      <Text type="secondary">{trip.destinationCity?.nameUz || trip.destinationDistrict?.nameUz || ''}</Text>
                    </Space>
                  </Descriptions.Item>
                  {trip.departureTime && (
                    <Descriptions.Item label="Jo'nash vaqti">{formatDate(trip.departureTime)}</Descriptions.Item>
                  )}
                  {trip.pricePerSeat && (
                    <Descriptions.Item label="Narx (1 joy)">{formatPrice(trip.pricePerSeat)}</Descriptions.Item>
                  )}
                </Descriptions>
              </Card>

              {/* Driver Info */}
              <Card title={<Space><CarOutlined /> Haydovchi</Space>} size="small">
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Ism">{trip.driver?.fullName || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Telefon">{trip.driver?.phone || '-'}</Descriptions.Item>
                  {trip.driver?.rating && (
                    <Descriptions.Item label="Reyting">{Number(trip.driver.rating).toFixed(1)}</Descriptions.Item>
                  )}
                  {trip.vehicle && (
                    <>
                      <Descriptions.Item label="Avtomobil">
                        {trip.vehicle.make} {trip.vehicle.model}
                      </Descriptions.Item>
                      <Descriptions.Item label="Raqam">{trip.vehicle.plate}</Descriptions.Item>
                      <Descriptions.Item label="Rang">{trip.vehicle.color}</Descriptions.Item>
                    </>
                  )}
                </Descriptions>
              </Card>
            </>
          )}
        </Col>
      </Row>

      {/* Cancel Reason Modal */}
      <Modal
        title={t('common.cancel')}
        open={cancelModalOpen}
        onCancel={() => {
          setCancelModalOpen(false);
          setCancelReason('Admin tomonidan bekor qilindi');
        }}
        onOk={handleCancel}
        okText={t('common.cancel')}
        cancelText={t('common.back')}
        okButtonProps={{ danger: true, loading: cancelling }}
      >
        <p style={{ marginBottom: 16 }}>Bekor qilish sababini kiriting:</p>
        <Input.TextArea
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          placeholder="Bekor qilish sababi..."
          rows={4}
        />
      </Modal>
    </div>
  );
};
